import { useState, useEffect } from "react";
import { usePatientTimer } from "@/hooks/room/usePatientTimer";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { useTranslation } from "react-i18next";
import { usePatientTimerContext } from "@/context/features/room/PatientTimerContext";
import { TypographyNumber } from "./numbers";
import { cn } from "@/lib/utils";
import { ClockIcon } from "lucide-react";
import { Badge } from "../ui/badge";

interface PatientTimerProps {
  userId?: string | null;
  isPatient: boolean;
}

export default function PatientTimer({ userId, isPatient }: PatientTimerProps) {
  const { t } = useTranslation();

  const {
    remainingSeconds,
    isMeetOver,
    showWarningDialog,
    timeColor,
    borderColor,
    bgColor,
    // resetForDev,
  } = usePatientTimer({ userId, isPatient });
  const [dialogOpen, setDialogOpen] = useState(false);
  const { addExpiredPatient, removeExpiredPatient } = usePatientTimerContext();

  useEffect(() => {
    if (showWarningDialog) {
      setDialogOpen(true);
    }
  }, [showWarningDialog]);

  useEffect(() => {
    if (isMeetOver && userId) {
      addExpiredPatient(userId);
    } else if (!isMeetOver && userId) {
      removeExpiredPatient(userId);
    }
  }, [isMeetOver, userId, addExpiredPatient, removeExpiredPatient]);

  if (!isPatient || !userId) {
    return null;
  }

  if (isMeetOver) {
    return (
      <Badge
        variant={"outline"}
        className="text-destructive border-destructive"
      >
        {t("meet_is_over")}
      </Badge>
    );
  }

  if (remainingSeconds == null) return null;

  const minutes = Math.floor(remainingSeconds / 60)
    .toString()
    .padStart(2, "0");
  const seconds = (remainingSeconds % 60).toString().padStart(2, "0");

  return (
    <>
      <Badge
        dir="ltr"
        variant={"outline"}
        className={cn("w-16 font-bold", timeColor, borderColor, bgColor)}
      >
        <TypographyNumber value={minutes} />:{" "}
        <TypographyNumber value={seconds} />
      </Badge>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader className="flex flex-row items-center  gap-2">
            <ClockIcon className=" text-destructive" size={20} />
            <DialogTitle className="text-destructive">
              {t("time_is_almost_over")}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center"></div>
          <p>{t("time_is_almost_over_desc")}.</p>
          <DialogFooter>
            <Button onClick={() => setDialogOpen(false)}>
              {t("I_understand")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
